import { useCallback } from 'react';

import { useCreatePayoutAccount } from '@givebacks/api/core';
import {
  ButtonMantine,
  Center,
  Group,
  ImageMantine,
  RadioMantine,
  Stack,
  Text,
  TextInput,
  notifications,
} from 'ui';
import { isNotEmpty, useForm } from 'ui/Form';
import { svg_hyperwallet } from 'ui/Icons';

type BankAccountForm = {
  routing_number: string;
  account_number: string;
  bank_account_purpose?: string;
};

const BankAccountForm = ({ onClose }: { onClose: () => void }) => {
  const { mutate: connectBank, isPending: isLoading } = useCreatePayoutAccount();

  const form = useForm<BankAccountForm>({
    initialValues: {
      routing_number: '',
      account_number: '',
    },
    validate: {
      routing_number: (value) => (value.trim().length === 0 ? 'Routing number is required' : null),
      account_number: (value) => (value.trim().length === 0 ? 'Account number is required' : null),
      bank_account_purpose: isNotEmpty('Bank account purpose is required'),
    },
  });

  const handleConnectAccount = useCallback(async () => {
    const { hasErrors } = form.validate();

    if (hasErrors) return;
    const { routing_number, account_number, bank_account_purpose } = form.values;
    if (!bank_account_purpose) return;
    connectBank(
      {
        accountType: 'bank',
        request: {
          bank_account: {
            account_number: account_number,
            routing_number: routing_number,
            bank_account_purpose: bank_account_purpose,
          },
        },
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Account Connected',
            message: 'Your account has been connected',
            variant: 'success',
          });
          onClose();
        },
        onError: () => {
          notifications.show({
            title: 'Error',
            message: 'There was an error connecting your account',
            variant: 'error',
          });
        },
      }
    );
  }, [form, connectBank, onClose]);

  return (
    <Stack gap="lg">
      <Center>
        <Group gap="xs">
          <Text fz={10}>Secured with</Text>
          <ImageMantine src={svg_hyperwallet} h={22} w="auto" />
        </Group>
      </Center>
      <Stack gap="xs">
        <TextInput {...form.getInputProps('routing_number')} placeholder="Routing Number" />
        <TextInput {...form.getInputProps('account_number')} placeholder="Account Number" />
      </Stack>
      {/* Designs do not show radio buttons but they are needed */}
      <RadioMantine.Group label="Account Type" {...form.getInputProps('bank_account_purpose')}>
        <Group>
          <RadioMantine value="CHECKING" label="Checking"></RadioMantine>
          <RadioMantine value="SAVINGS" label="Savings"></RadioMantine>
        </Group>
      </RadioMantine.Group>
      <ButtonMantine onClick={handleConnectAccount} loading={isLoading}>
        Connect Account
      </ButtonMantine>
    </Stack>
  );
};

export { BankAccountForm };
